import { useMemo, useRef, useEffect, useState } from 'react';

import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Link, useParams } from 'react-router-dom';

import { DocWithFacts } from '../../../../../../../../api/types/queries';
import { Doc, Folder } from '@/api/types/backend-dto-types';
import { cn } from '@/helpers/shadCnUtils';
import { Icon } from '@/new/components/icon';
import { ProcessingChip, getProcessingState, ProcessingState } from '@/new/components/processing-chip';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Empty, EmptyHeader, EmptyMedia, EmptyDescription, EmptyTitle } from '@/new/components/shadcn/empty';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/new/components/shadcn/tooltip';

type FolderRow = { kind: 'folder'; folder: Folder };
type DocRow = { kind: 'doc'; doc: DocWithFacts };
export type FileSystemRow = FolderRow | DocRow;

function rowId(row: FileSystemRow) {
  return row.kind === 'folder' ? `folder-${row.folder.id}` : row.doc.id;
}

const columnHelper = createColumnHelper<FileSystemRow>();

function buildColumns({
  searchQuery,
  hasContentFilters,
  selectedDocIds,
  onDocSelectionChange,
  matterId,
  subsetId,
  onFolderDownloadZip,
  onFolderCreateSubset,
}: {
  searchQuery: string;
  hasContentFilters: boolean;
  selectedDocIds: Set<string>;
  onDocSelectionChange: (docId: string, selected: boolean) => void;
  matterId: string;
  subsetId: string;
  onFolderDownloadZip: (folderId: string) => void;
  onFolderCreateSubset: (folderId: string) => void;
}) {
  return [
    columnHelper.display({
      id: 'select',
      size: 32,
      header: () => null,
      cell: ({ row }) => {
        if (row.original.kind === 'folder')
          return (
            <div className="ml-7 flex items-center gap-2">
              <Icon className="lucide--folder text-gray-400" aria-hidden />
            </div>
          );
        const doc = row.original.doc;
        const processingState = getProcessingState(doc);
        if (processingState === ProcessingState.NotProcessed) {
          return <Icon className="lucide--alert-circle ml-7 text-red-500" />;
        }
        return (
          <Checkbox
            className="ml-7 bg-white"
            checked={selectedDocIds.has(doc.id)}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(checked) => onDocSelectionChange(doc.id, checked === true)}
          />
        );
      },
    }),

    // Name (icon + label + badges)
    columnHelper.display({
      id: 'name',
      header: () => <span className="text-[11px]">Name</span>,
      cell: ({ row }) => {
        if (row.original.kind === 'folder') {
          const folder = row.original.folder;
          const folderName = folder.path.split('/').pop();
          const parentPath = folder.path.includes('/')
            ? folder.path.substring(0, folder.path.lastIndexOf('/'))
            : undefined;

          return (
            <span className="flex min-w-0 items-center gap-2">
              {searchQuery && parentPath && (
                <span className="truncate text-xs font-medium text-gray-400" title={folder.path}>
                  {parentPath}/
                </span>
              )}
              <span className="truncate text-xs font-medium text-gray-800" title={folder.path}>
                {folderName}
              </span>
            </span>
          );
        }

        const doc = row.original.doc;
        const processingState = getProcessingState(doc);

        const showFolderPath = hasContentFilters && Boolean(doc.file.folderPath);
        const fullPath = showFolderPath ? `${doc.file.folderPath}/${doc.file.name}` : doc.file.name;

        return (
          <span className="flex min-w-0 items-center gap-2 pt-px">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="max-w-72 truncate text-xs font-medium" aria-label={fullPath}>
                  {showFolderPath && (
                    <span className="text-gray-400">
                      {'../'.repeat(doc.file.folderPath!.split('/').filter(Boolean).length)}
                    </span>
                  )}
                  <span className="text-gray-800">{doc.file.name}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="text-xs">{fullPath}</p>
              </TooltipContent>
            </Tooltip>

            {processingState !== ProcessingState.Processed && (
              <ProcessingChip processingState={processingState} className="bg-opacity-0 rounded-sm border-none py-0" />
            )}

            {doc.file.fileMetadata?.relativityFields &&
              Object.keys(doc.file.fileMetadata.relativityFields).length > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center rounded bg-purple-100 px-1 py-0.5">
                        <Icon className="lucide--database h-2.5 w-2.5 text-purple-600" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p className="text-xs">Relativity Metadata Available</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

            {searchQuery && doc.chunkMatchCount > 0 && (
              <Link
                to={`/matters/${matterId}/subsets/${subsetId}/docs/${doc.id}?searchQuery=${encodeURIComponent(searchQuery)}`}
                className="shrink-0 rounded border border-teal-200 bg-teal-50 px-1.5 py-0.5 text-[10px] font-medium text-teal-700 transition-colors hover:border-teal-300 hover:bg-teal-100"
                onClick={(e) => e.stopPropagation()}
              >
                {doc.chunkMatchCount} {doc.chunkMatchCount === 1 ? 'match' : 'matches'}
              </Link>
            )}
          </span>
        );
      },
    }),

    // Date
    columnHelper.display({
      id: 'date',
      size: 90,
      header: () => <span className="text-[11px]">Document Date</span>,
      cell: ({ row }) => {
        if (row.original.kind === 'folder') return null;
        return <span className="truncate text-[11px] text-gray-500">{row.original.doc.dateText || '—'}</span>;
      },
    }),

    // Actions
    columnHelper.display({
      id: 'actions',
      size: 70,
      header: () => null,
      cell: ({ row }) => {
        if (row.original.kind === 'folder') {
          const folderId = row.original.folder.id;
          return (
            <div className="mr-2 flex items-center justify-end gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={(e) => e.stopPropagation()}>
                    <Icon className="lucide--ellipsis-vertical size-3.5 text-gray-500 hover:text-gray-900" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="p-1" align="end" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuItem className="flex gap-2 text-xs" onClick={() => onFolderCreateSubset(folderId)}>
                    <Icon className="lucide--group size-3" />
                    Create subset
                  </DropdownMenuItem>
                  <DropdownMenuItem className="flex gap-2 text-xs" onClick={() => onFolderDownloadZip(folderId)}>
                    <Icon className="lucide--download size-3" />
                    Download as zip
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        }

        const doc = row.original.doc;

        return (
          <div className="mr-4 flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" className="h-6 px-0 text-[11px]" asChild>
              <Link to={`/matters/${matterId}/subsets/${subsetId}/docs/${doc.id}`}>
                View
                <Icon className="lucide--arrow-right size-3.5" />
              </Link>
            </Button>
          </div>
        );
      },
    }),
  ];
}

const emptyData: FileSystemRow[] = [];

export function DocList({
  docs,
  folders,
  isLoading,
  searchQuery,
  selectedDoc,
  setSelectedDoc,
  selectedDocIds,
  onDocSelectionChange,
  onFolderClick,
  isInsideFolder,
  hasContentFilters,
  currentFolderPath,
  onNavigateUp,
  onFolderDownloadZip,
  onFolderCreateSubset,
}: {
  docs: DocWithFacts[] | undefined;
  folders: Folder[] | undefined;
  isLoading: boolean;
  searchQuery: string;
  selectedDoc: Doc | undefined;
  setSelectedDoc: (doc: DocWithFacts) => void;
  selectedDocIds: Set<string>;
  onDocSelectionChange: (docId: string, selected: boolean) => void;
  onFolderClick: (folderId: string) => void;
  isInsideFolder: boolean;
  hasContentFilters: boolean;
  currentFolderPath?: string;
  onNavigateUp: () => void;
  onFolderDownloadZip: (folderId: string) => void;
  onFolderCreateSubset: (folderId: string) => void;
}) {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const docRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  // Scroll selected doc into view
  useEffect(() => {
    if (selectedDoc?.id) {
      const el = docRefs.current.get(selectedDoc.id);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedDoc?.id]);

  // Merge folders + docs into a single data array
  // Keep the last known folder path so the breadcrumb doesn't flash away during loading
  const [displayedPath, setDisplayedPath] = useState<string | undefined>(currentFolderPath);
  const showBreadcrumb = isInsideFolder;

  useEffect(() => {
    if (currentFolderPath) {
      setDisplayedPath(currentFolderPath);
    }
  }, [currentFolderPath]);

  const data: FileSystemRow[] = useMemo(() => {
    const rows: FileSystemRow[] = [];
    if (folders) {
      for (const folder of folders) {
        rows.push({ kind: 'folder', folder });
      }
    }
    if (docs) {
      for (const doc of docs) {
        rows.push({ kind: 'doc', doc });
      }
    }
    return rows;
  }, [folders, docs]);

  const columns = useMemo(
    () =>
      buildColumns({
        searchQuery,
        hasContentFilters,
        selectedDocIds,
        onDocSelectionChange,
        matterId,
        subsetId,
        onFolderDownloadZip,
        onFolderCreateSubset,
      }),
    [
      searchQuery,
      hasContentFilters,
      selectedDocIds,
      onDocSelectionChange,
      matterId,
      subsetId,
      onFolderDownloadZip,
      onFolderCreateSubset,
    ],
  );

  const table = useReactTable({
    data: isLoading ? emptyData : data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => rowId(row),
    manualPagination: true, // pagination handled by parent
  });

  const hasRows = data.length > 0;

  // Count actual rows so we can continue the stripe pattern in filler rows
  const rowCount = data.length;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Folder breadcrumb — always mounted, animated via grid-rows */}
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-in-out',
          showBreadcrumb ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div
            className="flex cursor-pointer items-center gap-2.5 border-b border-gray-200 bg-gray-50/80 px-3 py-3 text-xs transition-colors hover:bg-gray-100"
            onClick={() => onNavigateUp()}
          >
            <Icon className="lucide--arrow-left text-sm text-gray-400" />
            <Icon className="lucide--folder text-sm text-gray-400" />
            <span className="pl-1 font-medium text-gray-600">{displayedPath ?? currentFolderPath}</span>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading
              ? Array.from({ length: 10 }).map((_, index) => (
                  <TableRow
                    key={`skeleton-${index}`}
                    className={cn('h-10 border-none', index % 2 === 1 && 'bg-gray-50')}
                  >
                    <TableCell className="py-2">
                      <Skeleton className="h-3.5 w-3.5 rounded" />
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-3.5 w-3.5 rounded" />
                        <Skeleton className="h-3.5 rounded" style={{ width: `${120 + Math.random() * 140}px` }} />
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <Skeleton className="h-3.5 w-14 rounded" />
                    </TableCell>
                    <TableCell className="py-2">
                      <Skeleton className="h-3.5 w-10 rounded" />
                    </TableCell>
                  </TableRow>
                ))
              : table.getRowModel().rows.map((row, index) => {
                  const original = row.original;
                  const isFolder = original.kind === 'folder';
                  const isSelectedDoc = !isFolder && original.doc.id === selectedDoc?.id;
                  const isNotProcessed = !isFolder && getProcessingState(original.doc) === ProcessingState.NotProcessed;
                  const isStripe = index % 2 === 1;

                  return (
                    <TableRow
                      key={row.id}
                      ref={(el) => {
                        if (!isFolder && el) {
                          docRefs.current.set(original.doc.id, el);
                        } else if (!isFolder) {
                          docRefs.current.delete(original.doc.id);
                        }
                      }}
                      className={cn(
                        'group cursor-pointer shadow-[inset_3px_0_0_0] shadow-transparent transition-colors hover:bg-gray-100/70!',
                        isStripe && 'bg-gray-50',
                        !isFolder && isSelectedDoc && 'shadow-teal-400',
                      )}
                      onClick={() => {
                        if (isFolder) {
                          onFolderClick(original.folder.id);
                        } else {
                          setSelectedDoc(original.doc);
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="py-2 [&>[role=checkbox]]:translate-y-0">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>

        {/* Fill remaining height with alternating stripes matching row height */}
        {hasRows && (
          <div
            ref={(el) => {
              if (!el) return;
              const row = el.previousElementSibling?.querySelector('tbody tr') as HTMLElement | null;
              if (row) {
                const h = row.offsetHeight;
                el.style.backgroundSize = `100% ${h * 2}px`;
                el.style.backgroundPositionY = `${rowCount % 2 === 0 ? 0 : h}px`;
              }
            }}
            className="flex-1 bg-[linear-gradient(to_bottom,_transparent_50%,_rgb(249_250_251)_50%)]"
          />
        )}

        {!isLoading && !hasRows && (
          <div className="flex flex-1 items-center justify-center px-6">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Icon className="lucide--circle-question-mark h-5 w-5" />
                </EmptyMedia>
                <EmptyTitle>{isInsideFolder ? 'This folder is empty' : 'No documents found'}</EmptyTitle>
                <EmptyDescription>
                  {isInsideFolder ? (
                    <div className="space-y-2">
                      <p>There are no documents or subfolders in this folder.</p>
                      <p className="text-xs text-gray-500">
                        This may be because files in this folder exist elsewhere and were deduplicated.
                      </p>
                    </div>
                  ) : (
                    <div>Could not find any documents in this subset.</div>
                  )}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        )}
      </div>
    </div>
  );
}
